import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { validateEmail } from "@/lib/utils/input-validator";
import { useTranslations, useLocale } from "next-intl";
import { translateBackendError } from "@/lib/i18n/translateBackendError";
import { requestRegistrationLink } from "@/lib/api/handlers/AuthenticationHandling";
import { useRateLimitCountdown } from "@/hooks/useRateLimitCountdown";
import { throwServerError } from "@/lib/api/errors/ErrorHandler";

/**
 * Email-first step 1 hook. Collects ONLY an email and fires a magic link.
 * The full form (password + account questions) lives on /auth/complete-signup,
 * reached by clicking the emailed link. Language is auto-detected from the
 * app locale and stored on the pending row server-side.
 */
export function useRegisterForm() {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const rateLimit = useRateLimitCountdown();
  const t = useTranslations("errors");
  const currentLocale = useLocale();
  const navigate = useNavigate();

  // Keep the countdown banner in sync with the ticking clock
  useEffect(() => {
    if (!rateLimit.isLimited) return;
    setEmailError(t("rateLimitedCountdown", { seconds: rateLimit.secondsLeft }));
  }, [rateLimit.secondsLeft, rateLimit.isLimited, t]);

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (emailError) setEmailError(validateEmail(value, t));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rateLimit.isLimited) return;

    const err = validateEmail(email, t);
    if (err) {
      setEmailError(err);
      return;
    }

    try {
      setIsLoading(true);
      const response = await requestRegistrationLink(email, currentLocale);

      if (
        response &&
        "isServerError" in response &&
        response.isServerError === true
      ) {
        if (response.message === "rateLimited" && response.retryAfter) {
          rateLimit.start(response.retryAfter);
        }
        throwServerError(response);
      }

      // Always advance to check-email — the backend resolves to 204 whether
      // the email is new or already registered (anti-enumeration).
      window.dispatchEvent(new Event("navigationStart"));
      navigate("/auth/check-email");
    } catch (error) {
      setEmailError(translateBackendError(t, error instanceof Error ? error : "unknown"));
    } finally {
      setIsLoading(false);
    }
  };

  return {
    email,
    emailError,
    isLoading,
    handleEmailChange,
    handleSubmit,
    rateLimited: rateLimit.isLimited,
  };
}
